import { BusinessObjectBase } from './base';
import { processDataPropertySchema } from '@schemas/business-objects/process-data-property';
import type { ProcessDataType } from './process-data-type';
import type { Id } from '../base-types';

export interface ProcessDataPropertyData {
  readonly id: Id;
  readonly name?: string;
  readonly isArray?: boolean;
  readonly description?: string;
  readonly sequence?: number;
  readonly dataTypeId: Id;
  readonly dataType?: ProcessDataType;
  readonly typeId: Id;
  readonly type?: ProcessDataType;
}

export interface ProcessDataProperty extends Readonly<ProcessDataPropertyData> {}
type ProcessDataPropertyCreateWithChildrenData =
  Partial<ProcessDataPropertyData>;

export class ProcessDataProperty extends BusinessObjectBase {
  static readonly entityName = 'ProcessDataProperty' as const;
  static readonly plural = 'process-data-properties';
  static schema = processDataPropertySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDataProperty[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDataProperty(data));
  }

  static async findOne(filter?: any): Promise<ProcessDataProperty | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDataProperty(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessDataProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDataProperty(data);
  }

  static async create(
    data: Omit<ProcessDataPropertyData, 'id'>,
  ): Promise<ProcessDataProperty> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDataProperty(result);
  }

  static async createMany(
    data: Omit<ProcessDataPropertyData, 'id'>[],
  ): Promise<ProcessDataProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataProperty(item));
  }

  static async createWithChildren(
    data: ProcessDataPropertyCreateWithChildrenData,
  ): Promise<ProcessDataProperty> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDataProperty(result);
  }

  static async createManyWithChildren(
    data: ProcessDataPropertyCreateWithChildrenData[],
  ): Promise<ProcessDataProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataProperty(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessDataPropertyData>,
  ): Promise<ProcessDataProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDataProperty(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
